import FooterButtons from "@/components/common/FooterButtons";
import ProgressBar from "@/components/common/ProgressBar";
import type { ButtonConfig } from "@/components/common/Complete";
import { AppPageLayout } from "@/components/common/AppPageLayout";
import { useCompletionLoadingState } from "@/components/common/useCompletionLoadingState";
import { NextButton } from "@/features/i18n/formComponents";
import {
    formPageColumnClass,
    formPageFooterActionsClass,
    formPageFooterFrameClass,
} from "@/features/pages/components/formPageLayout";
import { cn } from "@/lib/utils";
import { Button } from "@patient-watch/ui/button";
import { Progress } from "@patient-watch/ui/progress";
import Spinner from "@patient-watch/ui/spinner";
import { useMatches, useNavigate } from "@tanstack/react-router";
import { Check, X, type LucideIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

export type { ButtonConfig };

export interface JourneyProgressStep {
    label: string;
    /** When set on the current step, shown under the active bar instead of `label`. */
    subLabel?: string;
    state: "complete" | "current" | "upcoming";
    value?: number;
}

export interface JourneyProgressSummaryProps {
    title?: string;
    /** Localised prefix, e.g. "Current stage" / "Aktuelle Phase" */
    currentStagePrefix?: string;
    currentStageLabel?: string;
    currentPositionLabel?: string;
    stepProgressAriaLabel?: (stepLabel: string) => string;
    steps: readonly JourneyProgressStep[];
    className?: string;
    /**
     * `card` - pink/violet gradient callout (default).
     * `slim` - Typeform-style thin bars for compact demos.
     */
    variant?: "card" | "slim";
    /** Optional callout title under the bars (e.g. "All Caught Up"). */
    statusTitle?: string;
    /** Optional body under the bars (e.g. next due entry + reminder). */
    statusMessage?: string;
    /** Actions, profile progress, or other contextual content under the status. */
    children?: ReactNode;
    /** When set, shows a close control on the card (overview surfaces only). */
    onDismiss?: () => void;
    dismissLabel?: string;
}

export interface SuccessPageProps {
    title: string;
    description: string;
    icon?: LucideIcon;
    isLoading?: boolean;
    loadingText?: string;
    loadingDescription?: string;
    minLoadingTime?: number;
    loadingSteps?: readonly string[];
    stepIntervalMs?: number;
    primaryButton: ButtonConfig;
    secondaryButton?: (ButtonConfig | { text: string; onClick: () => void }) & {
        showWhen?: boolean;
    };
}

export interface CompletionShellProps extends Omit<SuccessPageProps, "primaryButton"> {
    /** Omit for a terminal screen with nothing to do next (e.g. a screener the patient does not pass). */
    primaryButton?: ButtonConfig;
    /** Extra content under the description, e.g. a QR code or a quiet link. */
    children?: ReactNode;
    /**
     * Short label under the success icon, e.g. "Profile saved".
     * Falls back to `title` when omitted (useful for clinician terminal screens).
     */
    statusLabel?: string;
    /** When set, shows the patient journey progress card above the success state. */
    journeyProgress?: JourneyProgressSummaryProps | null;
    /**
     * `inline` keeps the shared footer (primary beside secondary).
     * `stacked` is a full-width primary button with an outline secondary underneath.
     */
    actionsLayout?: "inline" | "stacked";
    /** Pins the actions to the bottom of the viewport, matching public form pages. */
    pinFooter?: boolean;
}

function JourneyProgressBars({
    steps,
    stepProgressAriaLabel,
    barClassName,
    gapClassName = "gap-1",
}: {
    steps: readonly JourneyProgressStep[];
    stepProgressAriaLabel: (stepLabel: string) => string;
    barClassName: string;
    gapClassName?: string;
}) {
    return (
        <div
            className={cn("grid", gapClassName)}
            style={{ gridTemplateColumns: `repeat(${Math.max(steps.length, 1)}, minmax(0, 1fr))` }}
        >
            {steps.map((step) => {
                const value = typeof step.value === "number" ? step.value : step.state === "complete" ? 100 : 0;

                return (
                    <Progress
                        key={step.label}
                        value={value}
                        className={barClassName}
                        aria-label={stepProgressAriaLabel(step.label)}
                    />
                );
            })}
        </div>
    );
}

export function JourneyProgressSummary({
    title = "Your progress",
    currentStagePrefix = "Current stage",
    currentStageLabel,
    currentPositionLabel,
    stepProgressAriaLabel = (stepLabel) => `${stepLabel} progress`,
    steps,
    className,
    variant = "card",
    statusTitle,
    statusMessage,
    children,
    onDismiss,
    dismissLabel = "Close progress",
}: JourneyProgressSummaryProps) {
    const activeStep = steps.find((step) => step.state === "current");
    const slimStageLabel = [currentStageLabel, activeStep?.subLabel].filter(Boolean).join(" · ");

    if (variant === "slim") {
        return (
            <div
                data-testid="journey-progress-summary"
                data-variant="slim"
                className={cn("space-y-1 w-full", className)}
            >
                {(slimStageLabel || currentPositionLabel) && (
                    <div className="flex justify-between items-baseline gap-3">
                        {slimStageLabel ? (
                            <p className="min-w-0 font-medium text-slate-600 dark:text-slate-300 text-sm truncate">
                                <span className="sr-only">{currentStagePrefix}: </span>
                                {slimStageLabel}
                            </p>
                        ) : (
                            <span />
                        )}
                        {currentPositionLabel ? (
                            <p className="font-medium tabular-nums text-slate-500 dark:text-slate-400 text-sm shrink-0">
                                {currentPositionLabel}
                            </p>
                        ) : null}
                    </div>
                )}
                {/* Same bar as form page progress, so the forms count reads as the same thing. */}
                <div
                    className="gap-1 grid"
                    style={{ gridTemplateColumns: `repeat(${Math.max(steps.length, 1)}, minmax(0, 1fr))` }}
                >
                    {steps.map((step) => (
                        <ProgressBar
                            key={step.label}
                            value={typeof step.value === "number" ? step.value : step.state === "complete" ? 100 : 0}
                            aria-label={stepProgressAriaLabel(step.label)}
                        />
                    ))}
                </div>
                {statusTitle || statusMessage || children ? (
                    <div className="space-y-2 pt-2">
                        {statusTitle || statusMessage ? (
                            <p className="min-w-0 text-2xs text-slate-700 dark:text-slate-200 leading-snug">
                                {statusTitle ? <span className="font-semibold">{statusTitle}</span> : null}
                                {statusMessage ? (
                                    <span className="font-normal text-slate-600 dark:text-slate-300">
                                        {statusTitle ? ": " : null}
                                        {statusMessage}
                                    </span>
                                ) : null}
                            </p>
                        ) : null}
                        {children}
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <div
            data-testid="journey-progress-summary"
            data-variant="card"
            className={cn(
                "space-y-3 shadow-lg p-4 border-2 border-dashed rounded-xl w-full",
                "bg-gradient-to-br from-violet-50 dark:from-violet-950/50 via-purple-50 dark:via-purple-950/50 to-fuchsia-50 dark:to-fuchsia-950/50",
                "border-violet-300/80 dark:border-violet-800/80",
                className
            )}
        >
            <div className="flex justify-between items-start gap-2">
                <p className="font-semibold text-violet-700 dark:text-violet-300 text-xs uppercase tracking-wide">
                    {title}
                </p>
                {onDismiss ? (
                    <button
                        type="button"
                        onClick={onDismiss}
                        className="flex justify-center items-center hover:bg-violet-200/70 dark:hover:bg-violet-900/60 -mt-1 -mr-1 rounded-md w-8 h-8 text-violet-700 dark:text-violet-200"
                        data-testid="journey-progress-dismiss"
                    >
                        <X size={16} strokeWidth={2.25} aria-hidden />
                        <span className="sr-only">{dismissLabel}</span>
                    </button>
                ) : null}
            </div>
            <div className="flex justify-between items-center gap-3">
                <p className="font-medium text-violet-900 dark:text-violet-100 text-sm">
                    {currentStagePrefix}
                    {currentStageLabel ? `: ${currentStageLabel}` : ""}
                </p>
                {currentPositionLabel ? (
                    <p className="font-medium text-violet-700/80 dark:text-violet-300/80 text-sm">
                        {currentPositionLabel}
                    </p>
                ) : null}
            </div>
            <div
                className="gap-2 grid"
                style={{ gridTemplateColumns: `repeat(${Math.max(steps.length, 1)}, minmax(0, 1fr))` }}
            >
                {steps.map((step) => {
                    const isActive = step.state === "current";
                    const value = typeof step.value === "number" ? step.value : step.state === "complete" ? 100 : 0;

                    return (
                        <div key={step.label} className="space-y-1.5 min-w-0">
                            <Progress
                                value={value}
                                className="bg-violet-200/80 [&>div]:bg-violet-600 dark:[&>div]:bg-violet-400 dark:bg-violet-900/50 h-1.5"
                                aria-label={stepProgressAriaLabel(step.label)}
                            />
                            <p
                                className={cn(
                                    "font-medium text-xs truncate",
                                    isActive
                                        ? "text-fuchsia-700 dark:text-fuchsia-300"
                                        : "text-violet-700/70 dark:text-violet-300/70"
                                )}
                            >
                                {isActive && step.subLabel ? step.subLabel : step.label}
                            </p>
                        </div>
                    );
                })}
            </div>
            {statusTitle || statusMessage || children ? (
                <div className="space-y-3 pt-3 border-violet-300/70 dark:border-violet-700/70 border-t border-dashed">
                    {statusTitle || statusMessage ? (
                        <p className="min-w-0 text-violet-900 dark:text-violet-100 text-sm leading-snug">
                            {statusTitle ? <span className="font-semibold">{statusTitle}</span> : null}
                            {statusMessage ? (
                                <span className="font-normal text-violet-800/90 dark:text-violet-200/90">
                                    {statusTitle ? ": " : null}
                                    {statusMessage}
                                </span>
                            ) : null}
                        </p>
                    ) : null}
                    {children}
                </div>
            ) : null}
        </div>
    );
}

const COMPLETION_BADGE_SIZE = "w-24 h-24 shrink-0";

const TEXT_FADE_MS = 220;

function useFadingCopy(headline: string, description = "") {
    const [visible, setVisible] = useState(true);
    const [copy, setCopy] = useState({ headline, description });
    const nextCopy = useRef({ headline, description });
    nextCopy.current = { headline, description };

    useEffect(() => {
        if (headline === copy.headline && description === copy.description) return;

        setVisible(false);
        const swap = window.setTimeout(() => {
            setCopy(nextCopy.current);
            setVisible(true);
        }, TEXT_FADE_MS);

        return () => window.clearTimeout(swap);
    }, [copy.description, copy.headline, description, headline]);

    return { visible, headline: copy.headline, description: copy.description };
}

function CompletionBadge({ icon: Icon, isLoading }: { icon?: LucideIcon; isLoading: boolean }) {
    const IconComponent = Icon ?? Check;

    return (
        <div className={cn("relative flex justify-center items-center", COMPLETION_BADGE_SIZE)}>
            <div
                className="absolute inset-0 bg-gradient-to-br from-pink-300/40 via-indigo-300/40 to-sky-300/40 shadow-md border border-white/30 rounded-2xl"
                aria-hidden
            />
            <div className="absolute inset-0 bg-gradient-to-br from-white/70 to-transparent rounded-2xl" aria-hidden />
            {isLoading ? (
                <Spinner inline className="w-10 h-10" />
            ) : (
                <div className="relative">
                    <div
                        className="absolute inset-0 bg-gradient-to-br from-indigo-500/40 to-pink-500/30 blur-lg"
                        aria-hidden
                    />
                    <IconComponent
                        className="relative drop-shadow-md w-10 h-10 text-indigo-600"
                        strokeWidth={2.5}
                        aria-hidden
                    />
                </div>
            )}
        </div>
    );
}

/**
 * Shared completion shell for patient journey and clinician terminal screens.
 * Journey progress is optional; success styling and actions stay consistent either way.
 */
export function CompletionShell({
    title,
    description,
    statusLabel,
    journeyProgress,
    icon,
    isLoading = false,
    loadingText,
    loadingDescription,
    loadingSteps,
    stepIntervalMs = 1000,
    minLoadingTime = 0,
    primaryButton,
    secondaryButton,
    actionsLayout = "inline",
    pinFooter = false,
    children,
}: CompletionShellProps) {
    const navigate = useNavigate();
    const matches = useMatches();
    const fillsParentShell = matches.some((match) => match.routeId === "/_app" || match.routeId === "/onboarding");
    const { showLoading, loadingHeadline } = useCompletionLoadingState({
        isLoading,
        minLoadingTime,
        loadingSteps,
        stepIntervalMs,
        loadingText,
        fallbackHeadline: title,
    });

    const resolvedStatusLabel = statusLabel ?? title;
    const fadingCopy = useFadingCopy(
        showLoading ? loadingHeadline : resolvedStatusLabel,
        (showLoading ? loadingDescription : description) ?? ""
    );

    const handlePrimary = useCallback(() => {
        if (!primaryButton) return;
        primaryButton.onClick?.();
        void navigate({
            to: primaryButton.to,
            ...(primaryButton.params != null ? { params: primaryButton.params } : {}),
            ...(primaryButton.search != null ? { search: primaryButton.search } : {}),
        });
    }, [navigate, primaryButton]);

    const handleSecondary =
        secondaryButton?.showWhen && "to" in secondaryButton
            ? () => {
                  secondaryButton.onClick?.();
                  void navigate({
                      to: secondaryButton.to,
                      ...(secondaryButton.params != null ? { params: secondaryButton.params } : {}),
                      ...(secondaryButton.search != null ? { search: secondaryButton.search } : {}),
                  });
              }
            : secondaryButton?.showWhen && "onClick" in secondaryButton
              ? secondaryButton.onClick
              : undefined;

    const actions = !primaryButton ? null : pinFooter ? (
        <div className={formPageFooterFrameClass}>
            <div className={formPageFooterActionsClass}>
                <div className="footer-actions-primary">
                    <NextButton
                        className="px-4 py-3 w-full h-auto min-h-14 text-balance leading-snug whitespace-normal"
                        label={primaryButton.text}
                        onClick={handlePrimary}
                        disabled={showLoading}
                        data-testid="footer-primary-button"
                    />
                </div>
                {secondaryButton?.showWhen && handleSecondary ? (
                    <div className="footer-actions-secondary">
                        <Button
                            size="lg"
                            type="button"
                            variant="secondary"
                            className="px-4 py-3 w-full h-auto min-h-14 text-balance leading-snug whitespace-normal"
                            disabled={showLoading}
                            onClick={handleSecondary}
                            data-testid="footer-secondary-button"
                        >
                            {secondaryButton.text}
                        </Button>
                    </div>
                ) : null}
            </div>
        </div>
    ) : actionsLayout === "stacked" ? (
        <div className="flex flex-col gap-3 mb-8 pt-2 w-full">
            <Button
                size="lg"
                type="button"
                className="w-full"
                disabled={showLoading}
                onClick={handlePrimary}
                data-testid="footer-primary-button"
            >
                {primaryButton.text}
            </Button>
            {secondaryButton?.showWhen && handleSecondary ? (
                <Button
                    size="lg"
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={showLoading}
                    onClick={handleSecondary}
                    data-testid="footer-secondary-button"
                >
                    {secondaryButton.text}
                </Button>
            ) : null}
        </div>
    ) : (
        <FooterButtons
            func1={handlePrimary}
            words1={primaryButton.text}
            primaryAction="next"
            type1="button"
            disabled1={showLoading}
            func2={handleSecondary}
            words2={secondaryButton?.showWhen ? secondaryButton.text : undefined}
            disabled2={showLoading}
        />
    );

    return (
        <AppPageLayout
            padding="none"
            className={cn(pinFooter && fillsParentShell && "flex flex-col flex-1 min-h-full")}
            data-testid="completion-shell"
        >
            <div
                className={
                    pinFooter
                        ? fillsParentShell
                            ? "flex flex-col flex-1 min-h-full"
                            : formPageColumnClass
                        : "flex flex-col gap-y-6 w-full"
                }
            >
                <div className={pinFooter ? "flex-1" : undefined}>
                    {journeyProgress ? <JourneyProgressSummary {...journeyProgress} /> : null}

                    <div aria-live="polite" aria-busy={showLoading} className="w-full">
                        <div className="flex flex-col items-center gap-6 py-8 text-center">
                            <CompletionBadge icon={icon} isLoading={showLoading} />
                            <div
                                className={cn(
                                    "space-y-3 w-full transition-opacity duration-300 ease-out",
                                    fadingCopy.visible ? "opacity-100" : "opacity-0"
                                )}
                            >
                                <h1 className="font-semibold text-slate-950 text-2xl sm:text-3xl tracking-tight">
                                    {fadingCopy.headline}
                                </h1>
                                {fadingCopy.description ? (
                                    <p
                                        className={cn(
                                            "text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line",
                                            pinFooter ? "text-lg" : "text-base sm:text-lg"
                                        )}
                                    >
                                        {fadingCopy.description}
                                    </p>
                                ) : null}
                            </div>
                            {children}
                        </div>
                    </div>
                </div>
                {actions}
            </div>
        </AppPageLayout>
    );
}

/**
 * Phase-complete screen for patient journey flows. Thin alias over CompletionShell
 * that keeps the existing call sites (`statusLabel` + journey progress).
 */
export function JourneyPhaseSuccessPage({
    statusLabel,
    journeyProgress,
    actionsLayout,
    pinFooter,
    ...props
}: SuccessPageProps & {
    statusLabel: string;
    journeyProgress?: JourneyProgressSummaryProps | null;
    actionsLayout?: "inline" | "stacked";
    pinFooter?: boolean;
}) {
    return (
        <CompletionShell
            {...props}
            statusLabel={statusLabel}
            journeyProgress={journeyProgress}
            actionsLayout={actionsLayout}
            pinFooter={pinFooter ?? true}
        />
    );
}

/** Shared success screen used after forms, consent, and other completed flows. */
export function SuccessPage(props: SuccessPageProps) {
    return <CompletionShell {...props} pinFooter />;
}

export function JourneySuccessPage({
    journeyProgress,
    ...successProps
}: SuccessPageProps & { journeyProgress: JourneyProgressSummaryProps }) {
    return <CompletionShell {...successProps} journeyProgress={journeyProgress} />;
}

export default SuccessPage;
